import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, useParams } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Send, Loader2 } from "lucide-react";
import type { Bot, Scenario, Duel, DuelRound } from "@shared/schema";

export default function HumanDuelPage() {
  const [, navigate] = useLocation();
  const { id } = useParams<{ id: string }>();
  const [message, setMessage] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: duel, isLoading: duelLoading } = useQuery<Duel>({
    queryKey: ["/api/duels", id],
  });

  const { data: bots = [] } = useQuery<Bot[]>({ queryKey: ["/api/bots"] });
  const { data: scenarios = [] } = useQuery<Scenario[]>({ queryKey: ["/api/scenarios"] });
  const { data: extBots = [] } = useQuery<{ id: string; name: string; avatar: string }[]>({
    queryKey: ["/api/external-bots/all"],
  });

  const bot = duel
    ? bots.find(b => b.id === duel.botAId) ||
      (duel.botAId.startsWith("ext:")
        ? (() => {
            const e = extBots.find(x => `ext:${x.id}` === duel.botAId);
            return e
              ? ({ id: duel.botAId, name: e.name, avatar: e.avatar, style: "", description: "", strategy: "" } as Bot)
              : undefined;
          })()
        : undefined)
    : undefined;
  const scenario = duel ? scenarios.find(s => s.id === duel.scenarioId) : undefined;
  const rounds: DuelRound[] = (duel?.rounds as DuelRound[]) || [];
  const maxRounds = scenario?.maxRounds || 8;

  const sendMutation = useMutation({
    mutationFn: async (msg: string) => {
      const res = await apiRequest("POST", "/api/duels/human-message", {
        duelId: id,
        message: msg,
      });
      return res.json();
    },
    onSuccess: () => {
      setMessage("");
      queryClient.invalidateQueries({ queryKey: ["/api/duels", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/duels"] });
      queryClient.invalidateQueries({ queryKey: ["/api/bots"] });
      queryClient.invalidateQueries({ queryKey: ["/api/external-bots/all"] });
    },
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [rounds.length]);

  const handleSend = () => {
    if (!message.trim() || sendMutation.isPending) return;
    sendMutation.mutate(message.trim());
  };

  if (duelLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Button variant="ghost" size="sm" className="mb-4 gap-1" onClick={() => navigate("/")} data-testid="button-back-human">
        <ArrowLeft className="w-4 h-4" /> Назад к арене
      </Button>

      {scenario && (
        <Card className="p-5 mb-6">
          <h1 className="text-lg font-semibold mb-1" data-testid="text-human-duel-title">{scenario.title}</h1>
          <p className="text-sm text-muted-foreground mb-3">{scenario.description}</p>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="bg-primary/5 rounded-md p-3">
              <span className="text-xs font-semibold text-primary">Ваша роль</span>
              <p className="text-xs mt-1">{scenario.roleA}</p>
              <p className="text-xs text-muted-foreground mt-1">Цель: {scenario.goalA}</p>
            </div>
            <div className="bg-muted/30 rounded-md p-3">
              <span className="text-xs font-semibold text-muted-foreground">Оппонент: {bot?.avatar} {bot?.name}</span>
              <p className="text-xs mt-1">{scenario.roleB}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Progress value={(rounds.length / maxRounds) * 100} className="flex-1" />
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              Раунд {rounds.length}/{maxRounds}
            </span>
          </div>
        </Card>
      )}

      {/* Messages */}
      <div className="space-y-3 mb-6 min-h-[200px]" data-testid="human-duel-messages">
        {rounds.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <p className="text-sm">Начните переговоры. Напишите первую реплику.</p>
          </div>
        )}

        {rounds.map((round, i) => (
          <div key={i}>
            {/* Human message */}
            <div className="flex justify-end mb-3">
              <div className="max-w-[80%]">
                <div className="flex items-center gap-2 mb-1 justify-end">
                  <span className="text-xs text-muted-foreground">Раунд {round.round}</span>
                  <span className="text-xs font-medium text-primary">Вы</span>
                  <span className="text-sm">👤</span>
                </div>
                <div className="rounded-lg px-4 py-3 text-sm bg-primary/10 border border-primary/20">
                  {round.speakerA}
                </div>
              </div>
            </div>
            {/* Bot message */}
            <div className="flex justify-start mb-3">
              <div className="max-w-[80%]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm">{bot?.avatar}</span>
                  <span className="text-xs font-medium text-blue-400">{bot?.name}</span>
                </div>
                <div className="rounded-lg px-4 py-3 text-sm bg-blue-500/10 border border-blue-500/20">
                  {round.speakerB}
                </div>
              </div>
            </div>
          </div>
        ))}

        {sendMutation.isPending && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <span className="text-sm">{bot?.avatar}</span>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-muted-foreground/50 typing-dot" />
              <div className="w-2 h-2 rounded-full bg-muted-foreground/50 typing-dot" />
              <div className="w-2 h-2 rounded-full bg-muted-foreground/50 typing-dot" />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      {duel?.status !== "completed" && rounds.length < maxRounds ? (
        <div className="sticky bottom-4">
          <Card className="p-3 flex gap-2">
            <Textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Ваша реплика в переговорах..."
              rows={2}
              className="resize-none flex-1"
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              data-testid="input-human-message"
            />
            <Button
              size="sm"
              className="self-end gap-1"
              disabled={!message.trim() || sendMutation.isPending}
              onClick={handleSend}
              data-testid="button-send-message"
            >
              <Send className="w-4 h-4" />
            </Button>
          </Card>
        </div>
      ) : (
        <Card className="p-6 text-center glow-amber">
          <Badge className="text-sm px-4 py-1 mb-3">Переговоры завершены</Badge>
          <p className="text-sm text-muted-foreground mb-4">
            Пройдено {rounds.length} раундов из {maxRounds}
          </p>
          <Button onClick={() => navigate("/")} data-testid="button-new-human-duel">
            Вернуться к арене
          </Button>
        </Card>
      )}
    </div>
  );
}
